import { CreatePaymentPlanDTO, OrderCustomerDTO, TenantDTO } from '@reposit/api-client';
import React, { useState } from 'react';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../containers/FlashMessage';
import { CREATE_PAYMENT_PLAN_STORE_KEY } from '../../redux/payment-plan/payment-plan.actions';
import { penceToPounds } from '../../utils/common.utils';
import FlashMessage from '../FlashMessage';
import Modal from '../Modal';
import { Header3, P1 } from '../Typography';
import PaymentPlanForm from './PaymentPlanForm';

interface PaymentPlanModalProps {
  onDismiss: () => void;
  totalBalance?: number;
  orderCustomers: OrderCustomerDTO[];
  tenants: TenantDTO[];
  onSubmit: (values: PaymentPlanFormValues) => Promise<unknown>;
  isSubmitting: boolean;
  showDeletedPaymentPlansInBalanceNotice: boolean;
  isClaimProposalPaymentPlanBlocked: boolean;
  isAmountOwedUnfinalised: boolean;
}

export enum MANUAL_PAYMENT_TAKEN {
  YES = 'YES',
  NO = 'NO',
}

export enum HISTORIC_PAYMENT_PLAN {
  YES = 'YES',
  NO = 'NO',
}

export enum INTEREST_ADDED {
  YES = 'YES',
  NO = 'NO',
}

export interface PaymentPlanFormValues {
  orderCustomerId: string;
  productType?: CreatePaymentPlanDTO['productType'];
  startDate?: Date;
  amount: number;
  instalments: number;
  manualPaymentsTaken: MANUAL_PAYMENT_TAKEN;
  isHistoric: HISTORIC_PAYMENT_PLAN;
  manualPaymentsTotal?: number;
  interestAdded: INTEREST_ADDED;
  interestAmount?: number;
}

const PaymentPlanModal: React.FC<PaymentPlanModalProps> = ({
  onDismiss,
  totalBalance,
  orderCustomers,
  tenants,
  onSubmit,
  isSubmitting,
  showDeletedPaymentPlansInBalanceNotice,
  isClaimProposalPaymentPlanBlocked,
  isAmountOwedUnfinalised,
}) => {
  const [manualPaymentsTotal, setManualPaymentsTotal] = useState(0);
  const [interestAmount, setInterestAmount] = useState(0);
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([CREATE_PAYMENT_PLAN_STORE_KEY]);
  const manualPaymentsPenceValue = manualPaymentsTotal * 100;
  const interestPenceValue = interestAmount * 100;
  return (
    <Modal onDismiss={onDismiss} scrollable fixHeight>
      {flashMessagePayload ? (
        <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 15 }}>Create Payment Plan</Header3>
      {isClaimProposalPaymentPlanBlocked ? (
        <P1>
          Payment plans cannot be created while a tenant claim proposal is awaiting payment. The tenant must pay the claim
          proposal balance in full before the supplier can respond.
        </P1>
      ) : isAmountOwedUnfinalised ? (
        <P1>
          Payment plans cannot be created until the final amount owed has been finalised. The amount is final once a claim
          proposal has been agreed or an arbitration award has been published.
        </P1>
      ) : totalBalance ? (
        <>
          <P1 style={{ fontWeight: 'bold' }}>
            The remaining balance is{' '}
            {
              <span>
                £{penceToPounds(totalBalance - manualPaymentsPenceValue + interestPenceValue)}
                {interestPenceValue ? ` (£${penceToPounds(interestPenceValue)} interest)` : ''}
              </span>
            }
            .
          </P1>
          {showDeletedPaymentPlansInBalanceNotice ? (
            <P1>This includes any previous paid payment plan instalments that have been deleted.</P1>
          ) : null}

          <PaymentPlanForm
            tenants={tenants}
            orderCustomers={orderCustomers}
            originalAmountRemaining={totalBalance}
            totalBalance={totalBalance - manualPaymentsPenceValue}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onDismiss={onDismiss}
            setManualPaymentsTotal={setManualPaymentsTotal}
            setInterestAmount={setInterestAmount}
            interestAmount={interestAmount}
          />
        </>
      ) : (
        <P1>This Claim is not in a payable state, therefore no Payment Plan can be created.</P1>
      )}
    </Modal>
  );
};

export default PaymentPlanModal;
